import { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, MapPin, Calendar, DollarSign, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConventionDatabase() {
  const [conventions, setConventions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchConventions();
  }, []);

  const fetchConventions = async (search = '') => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const url = search 
        ? `${BACKEND_URL}/api/convention-database?search=${encodeURIComponent(search)}`
        : `${BACKEND_URL}/api/convention-database`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConventions(response.data);
    } catch (error) {
      toast.error('Failed to load conventions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      fetchConventions(value);
    }
  };

  const handleAddToMyEvents = async (conv) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const eventData = {
        name: conv.name,
        location: conv.location,
        start_date: conv.start_date,
        end_date: conv.end_date,
        application_status: 'not_applied',
        approved_paid: false,
        staff_working: [],
        booth_cost: conv.estimated_booth_cost_min || 0,
        projected_revenue: 0,
        power_cost: 0,
        notes: conv.notes || '',
      };
      
      await axios.post(`${BACKEND_URL}/api/events`, eventData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(`${conv.name} added to your events!`);
    } catch (error) {
      toast.error('Failed to add to events');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading conventions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight" data-testid="convention-db-title">
          Convention Database
        </h1>
        <p className="text-muted-foreground mt-1">Discover conventions to attend in 2026</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search conventions by name, city, or state..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          data-testid="search-conventions-input"
          className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
        />
      </div>

      {conventions.length === 0 ? (
        <Card className="p-12 text-center bg-card border-border/50">
          <div className="text-muted-foreground">
            {searchTerm ? 'No conventions match your search' : 'No conventions in database'}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {conventions.map((conv) => (
            <Card
              key={conv.id}
              className="p-6 bg-card border-border/50 hover:border-primary/50 transition-colors"
              data-testid={`convention-${conv.id}`}
            >
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold font-heading mb-2">{conv.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{conv.location}</span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{conv.typical_dates}</span>
                  </div>
                  {conv.estimated_booth_cost_min > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      <span>
                        ${conv.estimated_booth_cost_min} - ${conv.estimated_booth_cost_max}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full capitalize">
                      {conv.convention_type}
                    </span>
                  </div>
                </div>

                {conv.notes && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{conv.notes}</p>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAddToMyEvents(conv)}
                    size="sm"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add to My Events
                  </Button>
                  {conv.website && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(conv.website, '_blank')}
                    >
                      Website
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
